import { useEffect, useRef } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { pushModal, popModal } from '@/store/ui';
import css from './Modal.module.css';

let modalIdCounter = 0;

/**
 * Универсальная модалка с focus-trap, Esc-стеком и ARIA.
 */
export function Modal({
  open,
  title,
  onClose,
  children,
  size = 'md',
  ariaLabel,
}) {
  const dialogRef = useRef(null);
  const triggerRef = useRef(null);
  const idRef = useRef(`modal-${++modalIdCounter}`);

  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement;
    const item = { id: idRef.current, onClose };
    pushModal(item);

    // Перевести фокус на модалку.
    const t = setTimeout(() => {
      const focusable = dialogRef.current?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      (focusable || dialogRef.current)?.focus();
    }, 0);

    return () => {
      clearTimeout(t);
      popModal(idRef.current);
      // Вернуть фокус к триггеру.
      if (triggerRef.current && triggerRef.current.focus) {
        triggerRef.current.focus();
      }
    };
  }, [open, onClose]);

  // Focus trap: Tab/Shift+Tab перепрыгивает в пределах модалки.
  function handleKeyDown(e) {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusable = Array.from(
      dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  if (!open) return null;

  // Портал в body: модалка должна жить вне .page (у неё transform → ломает
  // position:fixed и создаёт stacking-context, из-за чего лист уезжал под bottom-nav).
  return createPortal(
    <div
      class={css.overlay}
      role="presentation"
      onClick={(e) => {
        if (e.target.classList.contains(css.overlay)) onClose?.();
      }}
    >
      <div
        ref={dialogRef}
        class={`${css.modal} ${size === 'lg' ? css.modalLg : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || title || 'Диалог'}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        {title && (
          <div class={css.header}>
            <h2 class={css.title}>{title}</h2>
            <button
              type="button"
              class={css.close}
              onClick={onClose}
              aria-label="Закрыть"
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                fill="none"
                aria-hidden="true"
              >
                <path d="M6 6 L18 18 M18 6 L6 18" />
              </svg>
            </button>
          </div>
        )}
        <div class={css.body}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}
